// ── useVoiceConversationMode — modo de conversación por voz, manos libres ────
// Diseño aprobado (Eduardo, 2026-08-06): atajo abre y queda abierto (nada de
// mantener presionado — eso ya lo cubre push-to-talk en AssistantShell, esto
// es otra cosa). Adentro: VAD (Silero, corre en el browser vía
// @ricky0123/vad-web) detecta cuándo el usuario terminó de hablar, transcribe
// (mismo /api/assistant/transcribe que push-to-talk), manda solo, la
// respuesta se habla sola (ya lo hace AssistantContext.speakResponse, esto
// no reimplementa TTS), y vuelve a escuchar — sin tocar nada entre turnos.
//
// Sigue al módulo ACTIVO (useActiveAssistant, el mismo que lee AssistantDock)
// — si Eduardo navega mientras el modo está abierto, la conversación salta a
// lo que sea que esté activo ahora, igual que ya hace el dock. No es un
// bug, es el mismo modelo mental en todos lados.
//
// GATE DE SEGURIDAD (no negociable, pedido explícito): en cuanto aparece una
// propuesta pendiente (tool_proposal, acción gated — enviar, borrar, etc.),
// el VAD se pausa. Un "sí" mal transcripto no puede aprobar nada — la
// aprobación siempre es mouse. Ver hasPendingProposal abajo.
//
// Barge-in: el VAD sigue corriendo mientras habla (fase 'speaking') — si
// detecta voz real, corta la respuesta (stopSpeaking) e inmediatamente
// empieza a capturar lo que se está diciendo. echoCancellation:true en el
// stream ayuda a que no se dispare con su propia voz saliendo del parlante,
// pero no hay garantía dura entre hardware — mejor esfuerzo, no 100%.

import { useCallback, useEffect, useRef, useState } from 'react';
// Import dinámico a propósito (ver open() y handleSpeechEnd) — @ricky0123/vad-web
// arrastra el glue de onnxruntime-web (~124KB gzip, Vite lo marcó como chunk
// grande al buildear con import estático). Nadie que no abra el modo de voz
// tiene por qué pagar ese peso en cada carga de página. Solo el tipo es
// estático — se borra en compilación, cero costo de bundle.
import type { MicVAD } from '@ricky0123/vad-web';
import { useTranslation } from 'react-i18next';
import { useActiveAssistant, type EntityAssistantApi } from '../contexts/AssistantContext.js';
import { useSecretStatus } from './useSettings.js';
import { cargarModulo } from '../lib/moduloDinamico.js';

export type VoiceModePhase =
  | 'closed'
  | 'initializing'
  | 'listening'
  | 'processing'
  | 'speaking'
  | 'awaiting_approval'
  | 'mic_error';

export interface VoiceConversationApi {
  phase: VoiceModePhase;
  micError: string | null;
  open: () => void;
  close: () => void;
  pendingProposalCount: number;
}

const VAD_BASE_PATH = '/vad/';

function hasPendingProposal(api: EntityAssistantApi | null): boolean {
  if (!api) return false;
  return api.messages.some(m => m.role === 'tool_proposal' && m.proposal?.status === 'pending');
}

export function useVoiceConversationMode(): VoiceConversationApi {
  const { t } = useTranslation();
  const active = useActiveAssistant();
  const { data: groqStatus } = useSecretStatus('groq-api-key');
  const sttConfigured = groqStatus?.configured === true;

  const [isOpen, setIsOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micError, setMicError] = useState<string | null>(null);

  const micVadRef = useRef<MicVAD | null>(null);
  // El VAD se crea UNA vez y vive mientras el modo está abierto — los
  // callbacks necesitan siempre la entidad activa MÁS RECIENTE (cambia al
  // navegar), así que se lee de un ref en vez de recrear el VAD en cada
  // cambio de entidad (recrear reinicia el modelo ONNX, se nota).
  const activeApiRef = useRef<EntityAssistantApi | null>(active?.api ?? null);
  activeApiRef.current = active?.api ?? null;

  const pendingProposal = hasPendingProposal(active?.api ?? null);
  const streamingPhase = active?.api.streaming.phase ?? 'idle';
  const isSpeaking = active?.api.isSpeaking ?? false;

  const phase: VoiceModePhase = !isOpen
    ? 'closed'
    : micError != null
    ? 'mic_error'
    : isInitializing
    ? 'initializing'
    : pendingProposal
    ? 'awaiting_approval'
    : isTranscribing
    ? 'processing'
    : isSpeaking
    ? 'speaking'
    : streamingPhase !== 'idle'
    ? 'processing'
    : 'listening';

  // ── Transcribir + mandar — mismo endpoint que push-to-talk (useSpeechToText) ─
  const handleSpeechEnd = useCallback(async (audio: Float32Array) => {
    const api = activeApiRef.current;
    if (!api) return;
    setIsTranscribing(true);
    // Ya se cargó en open() — este import es cache hit, no vuelve a pedir red.
    const { utils: vadUtils } = await cargarModulo(() => import('@ricky0123/vad-web'));
    const wavBuffer = vadUtils.encodeWAV(audio);
    const form = new FormData();
    form.append('audio', new Blob([wavBuffer], { type: 'audio/wav' }), 'voice.wav');

    fetch('/api/assistant/transcribe', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: form,
    })
      .then(async r => {
        const data = await r.json().catch(() => ({})) as { text?: string; error?: string };
        if (!r.ok) throw new Error(data.error ?? `HTTP ${r.status}`);
        return data;
      })
      .then(data => {
        if (data.text?.trim()) void activeApiRef.current?.sendMessage(data.text);
      })
      .catch((err: unknown) => {
        setMicError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setIsTranscribing(false));
  }, []);

  // ── Barge-in — voz real detectada mientras habla: cortarla ya ────────────────
  const handleSpeechStart = useCallback(() => {
    if (activeApiRef.current?.isSpeaking) activeApiRef.current.stopSpeaking();
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setMicError(null);
    activeApiRef.current?.stopSpeaking();
    const vad = micVadRef.current;
    micVadRef.current = null;
    if (vad) void vad.destroy();
  }, []);

  const open = useCallback(() => {
    if (isOpen) return;
    if (!sttConfigured) {
      setMicError(t('assistant.voiceMode.micNotConfigured', {
        defaultValue: 'Configurá la clave de Groq en Ajustes para usar el modo de voz.',
      }));
      setIsOpen(true);
      return;
    }
    setMicError(null);
    setIsOpen(true);
    setIsInitializing(true);
    cargarModulo(() => import('@ricky0123/vad-web'))
      .then(({ MicVAD }) => MicVAD.new({
        model: 'v5',
        baseAssetPath: VAD_BASE_PATH,
        onnxWASMBasePath: VAD_BASE_PATH,
        startOnLoad: false,
        // Default de la librería es 1400ms — corta el turno en las pausas
        // naturales de deletrear un email o leer un número largo (reportado
        // por Eduardo: "rosaurabautista72@gmail.com" quedó en "rosaura 72").
        // Subido a 2200ms — más tolerante a esas pausas sin volverse lento
        // para una conversación normal. Si sigue cortando, subir más acá.
        redemptionMs: 2200,
        // Forzado a un solo hilo — el build de onnxruntime-web que se
        // self-hostea (ort-wasm-simd-threaded.wasm) intenta el runtime con
        // threads por default, que necesita SharedArrayBuffer (cross-origin
        // isolation, headers COOP/COEP). Sin esos headers falla al
        // inicializar ("no available backend found"). Forzar numThreads:1
        // en vez de agregar COOP/COEP a propósito — COEP:require-corp
        // rompería las imágenes cross-origin de los emails (imgSrc permite
        // https:/http: genérico, sin CORP de esos dominios).
        ortConfig: ortInstance => { ortInstance.env.wasm.numThreads = 1; },
        getStream: () => navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        }),
        onSpeechStart: handleSpeechStart,
        onSpeechEnd: audio => { void handleSpeechEnd(audio); },
        onVADMisfire: () => { /* voz muy corta, ni se transcribe — ruido normal */ },
      }))
      .then(vad => {
        micVadRef.current = vad;
        setIsInitializing(false);
      })
      .catch((err: unknown) => {
        setIsInitializing(false);
        setMicError(err instanceof Error ? err.message : String(err));
      });
  }, [isOpen, sttConfigured, handleSpeechStart, handleSpeechEnd]);

  // ── VAD corre solo en 'listening' y 'speaking' (barge-in) — se pausa en
  // 'processing' (nada que escuchar todavía) y en 'awaiting_approval' (gate
  // de seguridad: sin escucha, no hay forma de que una voz mal transcripta
  // apruebe una acción irreversible).
  useEffect(() => {
    const vad = micVadRef.current;
    if (!vad) return;
    const shouldRun = phase === 'listening' || phase === 'speaking';
    if (shouldRun && !vad.listening) void vad.start();
    if (!shouldRun && vad.listening) void vad.pause();
  }, [phase]);

  // Cierre al desmontar (navegación fuera de la app, etc.) — no dejar el mic
  // abierto huérfano.
  useEffect(() => () => { void micVadRef.current?.destroy(); }, []);

  // ── Atajo global: Ctrl/Cmd+Shift+Espacio abre/cierra. Escape cierra si está
  // abierto. Nuevo a propósito — Ctrl+Espacio ya es push-to-talk (AssistantShell).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'Space') {
        e.preventDefault();
        if (isOpen) close(); else open();
        return;
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, open, close]);

  return {
    phase,
    micError,
    open,
    close,
    pendingProposalCount: active?.api.messages.filter(m => m.role === 'tool_proposal' && m.proposal?.status === 'pending').length ?? 0,
  };
}
